import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { clearAppCacheAndReload } from "../lib/appRefresh";
import { useAuth } from "../context/AuthContext";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DOC_TYPES } from "../lib/constants";
import { CrossStandardPicker } from "../components/sop/CrossStandardPicker";
import { Sparkle, FloppyDisk, ClipboardText, FileText, Plus, ListChecks, ArrowsClockwise, ShieldStar } from "@phosphor-icons/react";

const SOP_TYPES = DOC_TYPES.filter((t) => ["procedure", "process", "plan", "program"].includes(t.value));

function CrosswalkTable({ rows }) {
  return (
    <div className="border border-border" data-testid="sop-crosswalk-table">
      <div className="border-b border-border px-3 py-2 bg-secondary/40 font-semibold text-sm">Crosswalk preview (also saved as the last section)</div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-muted-foreground"><th className="px-3 py-1.5">Requirement</th><th className="px-3 py-1.5">Addressed in</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.requirement_id} className="border-t border-border align-top" data-testid={`sop-crosswalk-row-${c.standard_id}-${c.req_number}`}>
              <td className="px-3 py-1.5 font-mono whitespace-nowrap">{c.standard_id} {c.req_number}{c.parts?.length ? ` (${c.parts.join(", ")})` : ""}</td>
              <td className={`px-3 py-1.5 ${c.sections.length ? "" : "text-red-700 font-semibold"}`}>{c.sections.join("; ") || "Not yet mapped — review"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SopGenerator() {
  const nav = useNavigate();
  const { aiEnabled } = useAuth();
  const { isModuleHidden } = useModuleVisibility();
  const controlsEnabled = !isModuleHidden("internal_controls");
  const showUpsell = isModuleHidden("approvals") || isModuleHidden("internal_controls");
  const [reqs, setReqs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [manuals, setManuals] = useState([]);
  const [controls, setControls] = useState([]);
  const [standards, setStandards] = useState([]);
  const [mode, setMode] = useState("requirement"); // requirement | standard | multi | topic
  const [reqId, setReqId] = useState("");
  const [standardId, setStandardId] = useState("");
  const [multiStdIds, setMultiStdIds] = useState([]);
  const [selection, setSelection] = useState({});
  const [topic, setTopic] = useState("");
  const [docType, setDocType] = useState("procedure");
  const [policyId, setPolicyId] = useState("none");
  const [controlId, setControlId] = useState("none");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [genError, setGenError] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null); // { title, doc_type, sections }

  useEffect(() => {
    api.get("/requirements").then((r) => setReqs(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/policy-manuals").then((r) => setManuals(Array.isArray(r.data) ? r.data : (r.data?.manuals || []))).catch(() => {});
    if (controlsEnabled) api.get("/controls", { __silent402: true }).then((r) => setControls(r.data)).catch(() => {});
    api.get("/standards").then((r) => setStandards(r.data)).catch(() => {});
  }, [controlsEnabled]);

  const generate = async () => {
    if (mode === "requirement" && !reqId) return toast.error("Pick a requirement");
    if (mode === "standard" && !standardId) return toast.error("Pick a standard");
    if (mode === "topic" && !topic.trim()) return toast.error("Describe a topic");
    if (mode === "multi" && Object.keys(selection).length < 2) return toast.error("Pick at least two requirements to combine");
    setBusy(true);
    setGenError("");
    try {
      const data = await runAiJob("/ai/generate-sop", {
        requirement_id: mode === "requirement" ? reqId : null,
        standard_id: mode === "standard" ? standardId : null,
        requirement_ids: mode === "multi" ? Object.keys(selection) : [],
        part_selections: mode === "multi" ? selection : {},
        topic: mode === "topic" ? topic : "",
        doc_type: docType,
        policy_id: policyId === "none" ? null : policyId,
        control_id: controlId === "none" ? null : controlId,
        instructions,
      });
      setDraft(data);
      toast.success("Draft generated — review and save");
    } catch (e) {
      setGenError(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };

  const setSection = (i, val) => {
    const sections = [...draft.sections];
    sections[i] = { ...sections[i], content: val };
    setDraft({ ...draft, sections });
  };

  const saveAsDocument = async () => {
    setSaving(true);
    try {
      // For standard-level SOPs, link to every requirement under that standard
      // so evidence & CFR coverage tracking treats the SOP as covering the whole family.
      let linked = [];
      if (mode === "requirement" && reqId) linked = [reqId];
      else if (mode === "standard" && standardId) {
        linked = reqs.filter((r) => r.standard_id === standardId).map((r) => r.id);
      } else if (mode === "multi") linked = draft.requirement_ids || Object.keys(selection);
      const { data } = await api.post("/documents", {
        title: draft.title,
        doc_type: draft.doc_type,
        sections: draft.sections,
        linked_requirements: linked,
        guided_by: policyId !== "none" ? [policyId] : [],
      });
      toast.success("Saved to Documents");
      nav(`/documents/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {showUpsell && (
        <div className="lg:col-span-3 flex items-center gap-3 border border-[#071B3E]/20 bg-[#071B3E]/[0.03] px-5 py-3"
          data-testid="sop-upsell-banner">
          <ShieldStar size={20} weight="fill" className="text-[#071B3E] shrink-0" />
          <div className="flex-1 text-sm text-foreground">
            <span className="font-semibold">Approvals &amp; Internal Controls are part of ClaroShield.</span>{" "}
            <span className="text-muted-foreground">Upgrade to add multi-signer review/approval workflows and internal-control tracking to your procedures.</span>
          </div>
          <Button size="sm" variant="outline" data-testid="sop-upsell-plans-btn"
            onClick={() => nav("/billing")} className="rounded-none shrink-0 border-[#071B3E]/30 text-[#071B3E] hover:bg-[#071B3E]/5">
            View plans
          </Button>
        </div>
      )}
      <div className="space-y-4">
        <div className="border border-border bg-card p-5 space-y-4">
          <div className="font-display font-bold flex items-center gap-2"><Sparkle size={16} weight="fill" />Draft a Procedure / Process</div>
          <div>
            <Label>Base it on</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="sop-mode-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="requirement">A NERC Requirement</SelectItem>
                <SelectItem value="standard">A NERC Standard (entire family)</SelectItem>
                <SelectItem value="multi">Multiple Standards (cross-standard SOP)</SelectItem>
                <SelectItem value="topic">A Free-text Topic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "requirement" ? (
            <div>
              <Label>Requirement</Label>
              <Select value={reqId} onValueChange={setReqId}>
                <SelectTrigger data-testid="sop-req-select" className="rounded-none mt-1"><SelectValue placeholder="Select requirement…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {reqs.map((r) => <SelectItem key={r.id} value={r.id}>{r.standard_id} {r.req_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : mode === "standard" ? (
            <div>
              <Label>Standard</Label>
              <Select value={standardId} onValueChange={setStandardId}>
                <SelectTrigger data-testid="sop-standard-select" className="rounded-none mt-1"><SelectValue placeholder="Select standard…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {standards.map((s) => <SelectItem key={s.id || s.standard_id} value={s.standard_id}>{s.standard_id} — {s.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                Drafts a program-style document covering <b>every requirement</b> under this standard (e.g. one Personnel Risk Assessment Program covering CIP-004-7 R1&mdash;R6).
              </p>
            </div>
          ) : mode === "multi" ? (
            <CrossStandardPicker reqs={reqs} standards={standards} stdIds={multiStdIds} setStdIds={setMultiStdIds}
              selection={selection} setSelection={setSelection} />
          ) : (
            <div>
              <Label>Topic</Label>
              <Input data-testid="sop-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Visitor escort during CIP access" className="rounded-none mt-1" />
            </div>
          )}
          <div>
            <Label>Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="sop-type-select" className="rounded-none mt-1 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>{SOP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Governing Policy <span className="text-muted-foreground">(optional)</span></Label>
            <Select value={policyId} onValueChange={setPolicyId}>
              <SelectTrigger data-testid="sop-policy-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">None</SelectItem>
                {manuals.length > 0 && <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Policy Manuals</div>}
                {manuals.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}{m.family ? ` (${m.family})` : ""}</SelectItem>)}
                {docs.length > 0 && <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Policy Documents</div>}
                {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {controlsEnabled && (
          <div>
            <Label>Related Control <span className="text-muted-foreground">(optional)</span></Label>
            <Select value={controlId} onValueChange={setControlId}>
              <SelectTrigger data-testid="sop-control-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">None</SelectItem>
                {controls.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}
          <div>
            <Label>Additional Instructions <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea data-testid="sop-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} className="rounded-none mt-1 font-mono text-sm" placeholder="Any specifics the AI should incorporate…" />
          </div>
          <Button data-testid="generate-sop-btn" onClick={generate} disabled={busy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} className="rounded-none w-full bg-primary hover:bg-primary/90">
            <Sparkle size={16} weight="fill" className="mr-1" />{busy ? "Drafting…" : "Generate Draft"}
          </Button>
          {genError && (
            <div data-testid="sop-gen-error" className="border border-red-300 bg-red-50 p-3 text-sm text-red-800 space-y-2">
              <div>Generation failed: {genError}</div>
              <div className="text-xs text-red-700">If this keeps happening, the app may be running a cached version. Refresh it and try again:</div>
              <button data-testid="sop-refresh-app-btn" onClick={clearAppCacheAndReload}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-900 underline">
                <ArrowsClockwise size={14} /> Refresh app &amp; retry
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        {!draft ? (
          <div className="border border-dashed border-border h-full min-h-[300px] flex items-center justify-center">
            <Empty title="No draft yet" hint="Configure the options and generate an AI draft to review here." />
          </div>
        ) : (
          <div className="border border-border bg-card" data-testid="sop-draft-preview">
            <div className="border-b border-border px-5 py-4 flex items-center justify-between gap-3">
              <Input data-testid="sop-draft-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="rounded-none border-0 border-b-2 border-black font-display font-bold text-lg px-0 h-auto py-1 focus-visible:ring-0" />
              <Button data-testid="save-sop-btn" onClick={saveAsDocument} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90 whitespace-nowrap">
                <FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Save as Document"}
              </Button>
            </div>
            <div className="p-5 space-y-4">
              {draft.sections.map((s, i) => (
                <div key={s.id} className="border border-border" data-testid={`sop-section-${i}`}>
                  <div className="border-b border-border px-3 py-2 bg-secondary/40 font-semibold text-sm">{s.title}</div>
                  <Textarea value={s.content} onChange={(e) => setSection(i, e.target.value)} className="rounded-none border-0 min-h-[120px] focus-visible:ring-0 font-mono text-sm" />
                </div>
              ))}
              {draft.crosswalk?.length > 0 && <CrosswalkTable rows={draft.crosswalk} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FormsList() {
  const nav = useNavigate();
  const { canEdit, aiEnabled } = useAuth();
  const [forms, setForms] = useState([]);
  const [standards, setStandards] = useState([]);
  const [open, setOpen] = useState(false);
  const [scenario, setScenario] = useState("");
  const [formType, setFormType] = useState("cxc");
  const [standardId, setStandardId] = useState("none");
  const [busy, setBusy] = useState(false);
  const [genError, setGenError] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = () => api.get("/forms").then((r) => setForms(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/standards").then((r) => setStandards(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    if (!scenario.trim()) return toast.error("Describe the scenario / purpose");
    setBusy(true);
    setGenError("");
    try {
      const data = await runAiJob("/ai/generate-form", {
        scenario, form_type: formType,
        standard_id: standardId === "none" ? null : standardId,
      });
      setDraft(data);
      toast.success(`Generated ${data.fields.length} fields — review and save`);
    } catch (e) {
      setGenError(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };

  const saveForm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/forms", draft);
      toast.success("Form template created");
      setOpen(false); setDraft(null); setScenario("");
      nav(`/forms/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const seedCxc = async () => {
    try {
      const { data } = await api.post("/forms/seed-cxc-template");
      toast.success("CXC Authorization Form added");
      nav(`/forms/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {canEdit && (
        <div className="mb-4 flex justify-end gap-2">
          <Button data-testid="seed-cxc-form-btn" onClick={seedCxc} variant="outline" className="rounded-none"><ClipboardText size={16} className="mr-1" />Standard CXC Authorization Form</Button>
          <Dialog open={open} onOpenChange={(v) => { if (!aiEnabled) return; setOpen(v); if (!v) setDraft(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-form-btn" disabled={!aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Form (AI)</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Generate a Form with AI</DialogTitle></DialogHeader>
              {!draft ? (
                <div className="space-y-4">
                  <div>
                    <Label>Form Type</Label>
                    <Select value={formType} onValueChange={setFormType}>
                      <SelectTrigger data-testid="form-type-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cxc">CIP Exceptional Circumstance (CXC)</SelectItem>
                        <SelectItem value="general">General Process / Documentation Form</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tie to a Standard <span className="text-muted-foreground">(optional — lets AI read its requirements & subparts)</span></Label>
                    <Select value={standardId} onValueChange={setStandardId}>
                      <SelectTrigger data-testid="form-standard-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="none">None</SelectItem>
                        {standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id} — {s.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scenario / Purpose</Label>
                    <Textarea data-testid="form-scenario-input" value={scenario} onChange={(e) => setScenario(e.target.value)} className="rounded-none mt-1 font-mono text-sm min-h-[100px]"
                      placeholder="e.g. Document a CIP Exceptional Circumstance: which requirement was affected, why it was declared, when it began/ended, who declared it, and mitigating steps." />
                  </div>
                  <Button data-testid="generate-form-btn" onClick={generate} disabled={busy} className="rounded-none w-full bg-primary hover:bg-primary/90">
                    <Sparkle size={16} weight="fill" className="mr-1" />{busy ? "Designing…" : "Generate Form"}
                  </Button>
                  {genError && (
                    <div data-testid="form-gen-error" className="border border-red-300 bg-red-50 p-3 text-sm text-red-800 space-y-2">
                      <div>Generation failed: {genError}</div>
                      <div className="text-xs text-red-700">If this keeps happening, the app may be running a cached version. Refresh it and try again:</div>
                      <button data-testid="form-refresh-app-btn" onClick={clearAppCacheAndReload}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-900 underline">
                        <ArrowsClockwise size={14} /> Refresh app &amp; retry
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4" data-testid="form-draft-preview">
                  <div>
                    <Label>Title</Label>
                    <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  {draft.purpose && <p className="text-sm text-muted-foreground">{draft.purpose}</p>}
                  <div className="border border-border divide-y divide-border">
                    {draft.fields.map((f) => (
                      <div key={f.id} className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2"><span className="font-semibold">{f.label}</span>
                          {f.required && <span className="text-red-600 text-xs">required</span>}
                          <span className="ml-auto font-mono text-xs text-muted-foreground">{f.field_type}</span>
                        </div>
                        {f.help && <div className="text-xs text-muted-foreground">{f.help}</div>}
                        {f.options?.length > 0 && <div className="text-xs text-muted-foreground mt-1">Options: {f.options.join(", ")}</div>}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setDraft(null)} className="rounded-none">Back</Button>
                    <Button data-testid="save-form-btn" onClick={saveForm} disabled={saving} className="rounded-none flex-1 bg-primary hover:bg-primary/90">
                      <FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Save & Edit Form"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
      {forms.length === 0 ? (
        <Empty title="No forms yet" hint="Generate a CXC or process documentation form with AI." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => (
            <button key={f.id} data-testid={`form-card-${f.id}`} onClick={() => nav(`/forms/${f.id}`)}
              className="text-left border border-border bg-card p-4 hover:border-black transition-colors">
              <div className="flex items-start gap-2">
                <ClipboardText size={18} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-display font-bold">{f.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{f.description || f.purpose}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                {f.form_type === "cxc" && <span className="border border-amber-400 bg-amber-50 text-amber-700 px-1.5 py-0.5 font-semibold uppercase">CXC</span>}
                <span>{f.fields?.length || 0} {(f.fields?.length || 0) === 1 ? "field" : "fields"}</span>
                <span className="ml-auto flex items-center gap-1"><ListChecks size={12} />{f.submission_count} filled</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SopForms() {
  return (
    <div>
      <PageHeader title="SOP & Forms" subtitle="AI-assisted procedures, processes and documentation forms" />
      <div className="p-8">
        <Tabs defaultValue="sop">
          <TabsList className="rounded-none">
            <TabsTrigger data-testid="tab-sop" value="sop" className="rounded-none gap-1"><FileText size={14} />SOP Generator</TabsTrigger>
            <TabsTrigger data-testid="tab-forms" value="forms" className="rounded-none gap-1"><ClipboardText size={14} />Forms</TabsTrigger>
          </TabsList>
          <TabsContent value="sop" className="mt-6"><SopGenerator /></TabsContent>
          <TabsContent value="forms" className="mt-6"><FormsList /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
